import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { HelpCircle, MessageCircle, Send, Bot, User, Ticket, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportPage = () => {
  const { token } = useAuth();
  const [faqs, setFaqs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: "Hi! I'm your Camsur Pickleball assistant. How can I help you today?" }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  
  // Ticket form state
  const [ticketForm, setTicketForm] = useState({ subject: '', message: '', category: 'General' });
  const [submittingTicket, setSubmittingTicket] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [faqsRes, ticketsRes] = await Promise.all([
          fetch(`${API}/support/faq`),
          fetch(`${API}/support/tickets`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          })
        ]);

        if (faqsRes.ok) setFaqs(await faqsRes.json());
        if (ticketsRes.ok) setTickets(await ticketsRes.json());
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await fetch(`${API}/support/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ message: chatInput })
      });

      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I'm having trouble right now. Please try again or create a support ticket." 
        }]);
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I couldn't process your request. Please try again." 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (!ticketForm.subject || !ticketForm.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmittingTicket(true);
    try {
      const response = await fetch(`${API}/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(ticketForm)
      });

      if (response.ok) {
        const ticket = await response.json();
        toast.success('Ticket submitted successfully');
        setTickets(prev => [ticket, ...prev]);
        setTicketForm({ subject: '', message: '', category: 'General' });
      } else {
        toast.error('Failed to submit ticket');
      }
    } catch (error) {
      toast.error('Failed to submit ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  const groupedFaqs = faqs.reduce((acc, faq) => {
    const category = faq.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(faq);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="support-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Help & Support</h1>
          <p className="text-muted-foreground">Get answers and assistance</p>
        </div>

        <Tabs defaultValue="chat">
          <TabsList className="mb-6">
            <TabsTrigger value="chat" data-testid="tab-chat">
              <MessageCircle className="h-4 w-4 mr-1" /> AI Chat
            </TabsTrigger>
            <TabsTrigger value="faq" data-testid="tab-faq">
              <HelpCircle className="h-4 w-4 mr-1" /> FAQ
            </TabsTrigger>
            <TabsTrigger value="tickets" data-testid="tab-tickets">
              <Ticket className="h-4 w-4 mr-1" /> My Tickets
            </TabsTrigger>
          </TabsList>

          {/* AI Chat */}
          <TabsContent value="chat">
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle className="flex items-center text-lg">
                  <Bot className="h-5 w-5 mr-2 text-primary" />
                  AI Support Assistant
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col p-0">
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {chatMessages.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`flex items-start space-x-2 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.role === 'user' ? 'bg-primary text-white' : 'bg-muted'
                          }`}>
                            {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                          </div>
                          <div className={`rounded-2xl px-4 py-2 ${
                            msg.role === 'user' 
                              ? 'bg-primary text-white' 
                              : 'bg-muted'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {chatLoading && (
                      <div className="flex justify-start">
                        <div className="flex items-center space-x-2">
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                            <Bot className="h-4 w-4" />
                          </div>
                          <div className="bg-muted rounded-2xl px-4 py-2">
                            <div className="flex space-x-1">
                              <div className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce"></div>
                              <div className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="h-2 w-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>
                <div className="p-4 border-t">
                  <div className="flex space-x-2">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="Type your question..."
                      onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
                      disabled={chatLoading}
                      data-testid="chat-input"
                    />
                    <Button 
                      onClick={handleSendChat} 
                      disabled={chatLoading || !chatInput.trim()}
                      className="btn-primary"
                      data-testid="send-chat-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ */}
          <TabsContent value="faq">
            <Card>
              <CardContent className="p-6">
                {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => (
                  <div key={category} className="mb-6 last:mb-0">
                    <h3 className="font-heading font-semibold mb-3">{category}</h3>
                    <Accordion type="single" collapsible>
                      {categoryFaqs.map((faq, idx) => (
                        <AccordionItem key={idx} value={`${category}-${idx}`}>
                          <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                          <AccordionContent className="text-muted-foreground">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                ))}
                {Object.keys(groupedFaqs).length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No FAQs available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tickets */}
          <TabsContent value="tickets">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Create Ticket */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Plus className="h-5 w-5 mr-2" />
                    Create New Ticket
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select 
                      value={ticketForm.category} 
                      onValueChange={(v) => setTicketForm({ ...ticketForm, category: v })}
                    >
                      <SelectTrigger data-testid="ticket-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="General">General</SelectItem>
                        <SelectItem value="Booking">Booking Issue</SelectItem>
                        <SelectItem value="Payment">Payment</SelectItem>
                        <SelectItem value="Technical">Technical</SelectItem>
                        <SelectItem value="Feedback">Feedback</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={ticketForm.subject}
                      onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                      placeholder="Brief description of your issue"
                      data-testid="ticket-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={ticketForm.message}
                      onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                      placeholder="Describe your issue in detail..."
                      rows={4}
                      data-testid="ticket-message"
                    />
                  </div>
                  <Button 
                    onClick={handleSubmitTicket}
                    disabled={submittingTicket}
                    className="w-full btn-primary"
                    data-testid="submit-ticket-btn"
                  >
                    {submittingTicket ? 'Submitting...' : 'Submit Ticket'}
                  </Button>
                </CardContent>
              </Card>

              {/* Tickets List */}
              <Card>
                <CardHeader>
                  <CardTitle>My Tickets</CardTitle>
                </CardHeader>
                <CardContent>
                  {tickets.length > 0 ? (
                    <div className="space-y-4">
                      {tickets.map((ticket) => (
                        <div key={ticket.ticket_id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium">{ticket.subject}</h4>
                            <Badge variant={ticket.status === 'open' ? 'default' : 'secondary'}>
                              {ticket.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{ticket.message}</p>
                          <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                            <span>{ticket.category}</span>
                            <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No tickets yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SupportPage;
